// @ts-nocheck
//
// INTERNAL / UNLISTED admin support tool — route /admin/impersonate2.
//
// Enter a user's email → mints a public-app `utoken` via the admin-gated
// endpoint POST /admins/impersonate → opens the public app's /impersonate
// landing (which sets the cookie on the app origin and reloads) in a new tab,
// so you view the public site AS that user for READ-ONLY troubleshooting.
//
// DELIBERATELY NOT in the sidebar/nav (Nav.tsx) and NOT linked anywhere —
// reachable only by typing the URL. Do NOT add it to the nav, and do NOT add
// it to apps/docs or any public documentation. Replaces the manual CLI script
// apps/api/api/scripts/16-login-as-user.ts for the common case.
import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { axios } from '@/store/axios';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { UserCheck, ExternalLink } from 'lucide-react';

const baseUrl = `${process.env.API_URL}/api/v1`;

// The public app origin to open. Locally the app is on :3000 (no subdomain);
// in prod it's the current org's subdomain (<slug>.staty.io). `league.name` is
// the org slug provided by OrganizationLayout's outlet context.
import { tenantOrigin, localTenantOrigin } from '@staty/shared/domains';

function appOriginFor(orgSlug: string): string {
  if (typeof window !== 'undefined' && window.location.hostname.includes('localhost')) {
    return localTenantOrigin();
  }
  return tenantOrigin(orgSlug);
}

export default function ImpersonatePage() {
  const { league } = (useOutletContext() as any) || {};
  const { getToken } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ name: string; email: string; url: string } | null>(null);

  const handleImpersonate = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/admins/impersonate`,
        { email: trimmed },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      if (!data || data.status !== 1 || !data.result?.token) {
        setError(data?.error || 'Could not create an impersonation session.');
        return;
      }
      const origin = appOriginFor(league?.name || '');
      const url = `${origin}/impersonate?token=${encodeURIComponent(data.result.token)}`;
      // Open the public app as that user in a new tab.
      window.open(url, '_blank', 'noopener');
      setResult({ name: data.result.user?.name || '', email: data.result.user?.email || trimmed, url });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Request failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer className="max-w-2xl">
      <PageHeader
        title="Impersonate User"
        description={(
          <>
            Internal support tool. Enter a user's email to open the public site as that user
            in a new tab. Use for <strong>read-only</strong> troubleshooting. Do not perform
            writes while impersonating.
          </>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>Login as user</CardTitle>
          <CardDescription>
            Opens <code>{appOriginFor(league?.name || '<org>')}</code> signed in as the entered user.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="email"
            inputMode="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleImpersonate(); }}
          />
          {error && <p className="text-sm text-destructive font-medium">{error}</p>}
          <Button onClick={handleImpersonate} disabled={loading || !email.trim()} size="lg">
            <UserCheck className={`h-4 w-4 mr-2 ${loading ? 'animate-pulse' : ''}`} />
            {loading ? 'Creating session…' : 'Impersonate'}
          </Button>

          {result && (
            <div className="rounded-lg border border-success/30 bg-success/10 p-3 text-sm">
              <p className="font-medium text-success">
                Opened a session for {result.name || result.email} ({result.email}).
              </p>
              <p className="text-success mt-1">
                If a tab didn't open (popup blocked),{' '}
                <a href={result.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                  click here <ExternalLink className="h-3 w-3" />
                </a>.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
